import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, NEVER, Observable } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    map,
    startWith,
    switchMap,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import {
    AUTOCOMPLETE_DEBOUNCE_TIME,
    BaseContainer,
    ComplexMapFieldValue,
    FormSelectOption,
    FormValue,
    FormValueState,
    isNotNullOrUndefined,
    LoadingBehaviorSubject,
    MultiFormSelectOptions,
    PartialMetadata
} from '@stylehaven/components';
import isEqual from 'lodash-es/isEqual';
import { WebserviceConfig } from '../../entities/webservice-config.entity';
import { ExternalWebservice } from '../../services/external-webservice.service';

@Component({
    standalone: false,
    selector: 'med-webservice-search',
    templateUrl: './webservice-search.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ExternalWebservice]
})
export class WebserviceSearchComponent extends BaseContainer implements OnInit, OnDestroy {
    public lookupLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();
    public options$: Observable<MultiFormSelectOptions<ComplexMapFieldValue>>;

    private search$: BehaviorSubject<string> = new BehaviorSubject('');

    @Input() searchControl: UntypedFormControl = new UntypedFormControl();
    @Input() clearOnSelect = false;
    @Input() indeterminateMsg: string;
    @Input() disabledMsg?: string;

    @Input() webserviceConfig: WebserviceConfig;

    @Output() selected: EventEmitter<ComplexMapFieldValue | null> = new EventEmitter<ComplexMapFieldValue | null>();

    constructor(private webservice: ExternalWebservice) {
        super();
    }

    ngOnInit(): void {
        if (!this.webserviceConfig) {
            console.error('WebserviceSearchComponent:: No webservice config provided');
            return;
        }

        this.initWebserviceResults();
        this.initValueListener();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.lookupLoading$.complete();
    }

    public initValueListener(): void {
        this.searchControl.valueChanges
            .pipe(
                isNotNullOrUndefined(),
                distinctUntilChanged(isEqual),
                map((values: FormValue<PartialMetadata>[]) => (values?.length > 0 ? values : [])),
                map(values => this.filterSelectedValue(values)),
                distinctUntilChanged(isEqual),
                takeUntil(this.destroy$)
            )
            .subscribe(values => {
                this.onSelection(values);
            });
    }

    private filterSelectedValue(values: FormValue<PartialMetadata>[]): PartialMetadata | null {
        const hasIndeterminate: boolean =
            values.filter(value => value.state === FormValueState.INDETERMINATE).length >= 1;

        if (hasIndeterminate) {
            return null;
        }

        const selected: PartialMetadata | null =
            values.filter(value => value.state === FormValueState.SET)[0]?.value ?? null;
        return selected;
    }

    private onSelection(selected: ComplexMapFieldValue | null): void {
        this.selected.next(selected);

        if (this.clearOnSelect) {
            this.searchControl.setValue([]);
            this.search$.next('');
        }
    }

    private initWebserviceResults(): void {
        this.webservice.initWebservice(this.webserviceConfig);

        const currentFormValues$: Observable<FormValue<Record<string, any>>[]> = this.searchControl.valueChanges.pipe(
            map((values: FormValue<Record<string, any>>[]) => values ?? []),
            distinctUntilChanged(isEqual),
            startWith([])
        );

        this.options$ = this.search$.pipe(
            distinctUntilChanged(isEqual),
            tap(() => this.lookupLoading$.startLoading()),
            debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME),
            switchMap(searchTerm => this.webservice.globalSearch(searchTerm)),
            withLatestFrom(this.search$, currentFormValues$),
            map(([concepts, searchTerm, values]) => this.metadataToFormSelectOptions(concepts, searchTerm, values)),
            tap(() => this.lookupLoading$.stopLoading()),
            catchError(() => {
                this.lookupLoading$.stopLoading();
                return NEVER;
            })
        );
    }

    private metadataToFormSelectOptions(
        searchResults: ComplexMapFieldValue[],
        searchTerm: string,
        controlValues: FormValue<ComplexMapFieldValue>[]
    ): MultiFormSelectOptions<ComplexMapFieldValue> {
        const noLabelFoundMsg: string = this.translateService.translate('complex.label-not-found');
        const matchingMissingSelectedValues: FormValue<ComplexMapFieldValue>[] = controlValues
            .filter(formValue => formValue.state === FormValueState.SET)
            .filter((formValue: FormValue<ComplexMapFieldValue>) => {
                const matchesSearch: boolean = this.webserviceConfig
                    .getWebserviceDataLabel(formValue.value, noLabelFoundMsg)
                    .toLowerCase()
                    .startsWith(searchTerm.toLowerCase());
                const notInResults = !searchResults.find(result => isEqual(result, formValue.value));
                return matchesSearch && notInResults;
            });

        const matchingMissingSelectedOptions: FormSelectOption<ComplexMapFieldValue>[] =
            matchingMissingSelectedValues.map(formValue => ({
                label: this.webserviceConfig.getWebserviceDataLabel(formValue.value, noLabelFoundMsg),
                value: formValue.value
            }));

        const searchResultOptions: FormSelectOption<ComplexMapFieldValue>[] = searchResults.map(result => ({
            label: this.webserviceConfig.getWebserviceDataLabel(result, noLabelFoundMsg),
            value: result
        }));

        return {
            options: [...matchingMissingSelectedOptions, ...searchResultOptions],
            isLiveData: true
        };
    }

    public onSearchTermChange(term: string): void {
        this.search$.next(term);
    }
}
